import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

/**
 * ApprovalEmailPreviewDialog
 *
 * Fetches the exact HTML email a student will receive on approval, renders
 * it inside a sandboxed iframe so the email's own <html>/<body> styles stay
 * scoped, and shows the To / Subject / Link admins should expect. Read-only —
 * no side effects.
 *
 * Props:
 *   open, onOpenChange
 *   campaignId  — required when open
 *   title       — campaign title (for the modal header)
 */
export const ApprovalEmailPreviewDialog = ({ open, onOpenChange, campaignId, title }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null); // { to, subject, html, link, projected_ends_at }
  const [copied, setCopied] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!open || !campaignId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    setCopied(false);
    client
      .get(`/admin/campaigns/${campaignId}/approval-email-preview`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.detail ||
            "Couldn't load the email preview. Try refreshing the queue and opening it again."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, campaignId]);

  // Push the HTML into the iframe via srcdoc for full sandboxing.
  useEffect(() => {
    if (!data?.html || !iframeRef.current) return;
    iframeRef.current.srcdoc = data.html;
  }, [data]);

  const copyLink = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard may be unavailable on http — silently no-op */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="approval-email-preview-dialog"
        className="max-w-2xl border-border bg-sand p-0 gap-0 sm:rounded-lg overflow-hidden"
      >
        <div className="relative overflow-hidden bg-beige px-8 py-6">
          <span className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-yellow/40 blur-3xl" />
          <span className="pointer-events-none absolute -left-8 -bottom-10 h-32 w-32 rounded-full bg-tangerine/25 blur-3xl" />
          <p className="relative text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
            Preview · read-only
          </p>
          <DialogTitle className="relative mt-1 font-serif text-3xl font-normal leading-[1.05] text-ink">
            The email {data?.to ? "they" : "the student"} will receive
          </DialogTitle>
          <DialogDescription className="relative mt-2 text-sm leading-relaxed text-olive">
            This is exactly what goes out the moment you click{" "}
            <span className="font-semibold text-ink">Approve</span> on{" "}
            <span className="font-semibold text-ink">{title || "this campaign"}</span>.
            Nothing sends from this window.
          </DialogDescription>
        </div>

        <div className="px-8 py-6 space-y-5">
          {loading && (
            <div className="flex items-center gap-2 text-olive" data-testid="preview-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Rendering the email…
            </div>
          )}

          {error && (
            <p
              role="alert"
              data-testid="preview-error"
              className="flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} /> {error}
            </p>
          )}

          {!loading && !error && data && (
            <>
              {/* Envelope: To / Subject / Link */}
              <dl
                data-testid="preview-envelope"
                className="grid grid-cols-[80px,1fr] gap-x-4 gap-y-2 rounded-md border border-border bg-bone px-4 py-3 text-sm"
              >
                <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive pt-0.5">
                  To
                </dt>
                <dd className="text-ink break-all" data-testid="preview-to">
                  {data.to}
                </dd>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive pt-0.5">
                  Subject
                </dt>
                <dd className="text-ink" data-testid="preview-subject">
                  {data.subject}
                </dd>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive pt-0.5">
                  Link
                </dt>
                <dd className="flex items-baseline gap-2 min-w-0">
                  <span className="truncate text-olive" data-testid="preview-link">
                    {data.link}
                  </span>
                  <button
                    type="button"
                    onClick={copyLink}
                    data-testid="preview-copy-link"
                    className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-sand px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-olive hover:border-ink hover:text-ink transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3" strokeWidth={2.5} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" strokeWidth={2.5} /> Copy
                      </>
                    )}
                  </button>
                </dd>
              </dl>

              {/* Rendered email body */}
              <div className="rounded-md border border-border bg-bone">
                <iframe
                  ref={iframeRef}
                  title="Approval email preview"
                  sandbox=""
                  data-testid="preview-iframe"
                  className="h-[420px] w-full rounded-md bg-white"
                />
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="preview-close"
              className="rounded-full border border-border bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
